/**
 * useProductIcon - lazy-resolves an `@atlaskit/icon/core/*` (or `icon-lab/core/*`)
 * component from a registry-supplied importPath.
 *
 * Returns the cached component synchronously when already loaded; otherwise
 * kicks off the dynamic import and triggers a re-render once it resolves.
 * Returns null while loading or if the importPath isn't present in the
 * generated icon importer maps.
 */
import { type ComponentType, useEffect, useState } from 'react';

import { getLoadedProductIcon, loadProductIcon } from './productIcons';

export function useProductIcon(importPath: string | null | undefined): ComponentType<any> | null {
	const [, setVersion] = useState(0);

	const cached = importPath ? getLoadedProductIcon(importPath) : null;

	useEffect(() => {
		if (!importPath || cached) {
			return;
		}

		let cancelled = false;
		loadProductIcon(importPath).then((Comp) => {
			if (cancelled) {
				return;
			}
			if (Comp) {
				// Force a re-render so the consumer picks up the now-cached icon.
				setVersion((v) => v + 1);
			}
		});

		return () => {
			cancelled = true;
		};
	}, [importPath, cached]);

	return cached;
}
